import { Component, computed, DestroyRef, inject, input, output, signal, Signal } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { Store } from '@ngrx/store'
import { ZipSelection } from '@ziptility/map/controls'
import { MapboxAPIService } from '@ziptility/modules'
import { IBvMap } from '@ziptility/modules/bv-api/models'
import { getAppPreference } from '@ziptility/store'
import { first } from 'lodash/fp'
import { CircleLayerSpecification, LngLatBoundsLike, Map, MapMouseEvent, SymbolLayerSpecification } from 'mapbox-gl'
import { Observable, firstValueFrom } from 'rxjs'
import { filter as rxFilter, map as rxMap, take } from 'rxjs/operators'
import { CommonModule } from '@angular/common'
import { IconsModule } from '@ziptility/icons'
import { BoardsStore, BoardTask } from '../boards/boards.store'
import { TaskListItemComponent } from '@ziptility/components/task-list-item/task-list-item.component'
import { TaskBoardType, TaskStatus } from '@ziptility/graphql'
import { userAvatarColorMap } from '@ziptility/components/user-avatar'
import { Feature, Point } from '@turf/turf'
import moment from 'moment'
import { getTaskDueDateUrgency, makeTaskDueDateLabel } from '@ziptility/utils/format'
import { GeoJSONSourceComponent, LayerComponent, MapComponent, PopupComponent } from 'ngx-mapbox-gl'
import { Task } from '@ziptility/graphql'
import { BvPreference } from '@ziptility/models'

@Component({
  selector: 'task-list-map',
  template: `
    <div class="w-full h-full relative">
      @if (map) {
        <div id="zoom-controls">
          <div class="control rounded " matTooltip="Zoom to extent" placement="right" (click)="onResetCenter()">
            <i-feather name="crosshair" class="w-2"></i-feather>
          </div>
          <div
            class="control  mt-2 border-b-1 border-gray-50 rounded-tr rounded-tl"
            placement="right"
            (click)="map.zoomIn()"
          >
            <i-feather name="plus"></i-feather>
          </div>
          <div class="control  mb-2 rounded-br rounded-bl" placement="right" (click)="map.zoomOut()">
            <i-feather name="minus"></i-feather>
          </div>
        </div>
      }

      <mgl-map
        [style]="'mapbox://styles/mapbox/satellite-streets-v11'"
        [bounds]="initialBounds$ | async"
        [dragRotate]="false"
        (mapLoad)="_onMapLoad($event.target)"
        (mapClick)="onMapClick($event)"
      >
        @if (taskPopup(); as taskPopup) {
          <mgl-popup
            [lngLat]="[taskPopup.geometry.coordinates[0], taskPopup.geometry.coordinates[1]]"
            [closeOnClick]="false"
            [closeButton]="false"
            maxWidth="none"
          >
            <div class="w-[300px]">
              <task-list-item
                class="cursor-pointer"
                [task]="taskPopup"
                [showBoard]="true"
                [showSelection]="false"
                [flexDirection]="'col'"
              ></task-list-item>
            </div>
          </mgl-popup>
        }

        @if (source(); as source) {
          <mgl-geojson-source id="tasks" [data]="source" promoteId="id"> </mgl-geojson-source>
          @for (styleLayer of styleLayers; track styleLayer.id) {
            <mgl-layer
              [id]="styleLayer.id"
              [source]="styleLayer.source"
              [type]="styleLayer.type"
              [paint]="styleLayer.paint"
              [layout]="styleLayer.layout"
              [filter]="styleLayer.filter"
              (layerClick)="onClickLayer($event)"
              (layerMouseEnter)="onMouseEnterLayer($event)"
              (layerMouseLeave)="onMouseLeaveLayer()"
            ></mgl-layer>
          }
        }
      </mgl-map>
    </div>
  `,
  styles: [
    `
      :host {
        width: 100%;
        height: 100%;
      }
      mgl-map {
        height: 100%;
        width: 100%;
      }

      #zoom-controls {
        border-radius: 3px;
        position: absolute;
        left: 23px;
        bottom: 40px;
        width: 35px;
        z-index: 10;
      }

      .control {
        width: 35px;
        height: 35px;
        display: flex;
        justify-content: center;
        text-align: center;
        align-items: center;
        cursor: pointer;
        background-color: white;
      }

      .control:hover {
        background-color: #eee;
      }
    `,
  ],
  imports: [
    CommonModule,
    IconsModule,
    MapComponent,
    PopupComponent,
    GeoJSONSourceComponent,
    LayerComponent,
    TaskListItemComponent,
  ],
})
export class TaskMap {
  readonly store = inject(Store)
  readonly router = inject(Router)
  readonly route = inject(ActivatedRoute)
  readonly mapboxService = inject(MapboxAPIService)
  readonly taskBoardStore = inject(BoardsStore)
  readonly destroyRef = inject(DestroyRef)
  readonly preference$ = this.store.select(getAppPreference)

  readonly source: Signal<GeoJSON.FeatureCollection<GeoJSON.Geometry>> = computed(() => {
    const features = this.taskBoardStore
      .swimlanes()
      .reduce<Feature<Point, { [name: string]: unknown }>[]>((arr, swimlane) => {
        const letter = first(swimlane.board.name)?.toLowerCase()
        const color = swimlane.board.type === TaskBoardType.User && letter ? userAvatarColorMap[letter] : undefined
        const featureCollection = this.makeFeatureCollection(swimlane.tasks, color)
        return arr.concat(featureCollection.features)
      }, [])
    return {
      type: 'FeatureCollection',
      features,
    }
  })

  private readonly selections = signal<string[]>([])

  readonly selectedMap = input<IBvMap>()
  readonly onSelections = output<{ ids: string[] }>()
  readonly taskPopup = signal<Task | undefined>(undefined)

  readonly mapSelectionManager = new ZipSelection()
  map!: Map

  readonly styleLayers: (SymbolLayerSpecification | CircleLayerSpecification)[] = [
    {
      id: 'task-feature-state',
      type: 'circle',
      source: 'tasks',
      paint: {
        'circle-radius': 20,
        'circle-color': 'white',
        'circle-opacity': ['case', ['==', ['feature-state', 'hover'], true], 0.5, 0],
      },
    },
    {
      id: 'tasks',
      type: 'circle',
      source: 'tasks',
      paint: {
        'circle-radius': 8,
        'circle-color': ['get', 'color'],
        'circle-stroke-color': 'white',
        'circle-stroke-width': 2,
      },
    },
    {
      id: 'task-count',
      type: 'symbol',
      source: 'tasks',
      layout: {
        'text-field': ['get', 'text'],
        'text-justify': 'center',
        'text-size': 10,
        'text-allow-overlap': true,
      },
      paint: {
        'text-color': 'white',
        'text-opacity-transition': {
          duration: 0,
          delay: 0,
        },
        'text-translate-transition': {
          duration: 0,
          delay: 0,
        },
      },
    },
  ]

  readonly initialBounds$: Observable<LngLatBoundsLike> = this.preference$.pipe(
    rxFilter(
      (p: BvPreference | undefined): p is BvPreference & { mapBounds: NonNullable<BvPreference['mapBounds']> } => {
        return !!p && !!p.mapBounds
      },
    ),
    rxMap((p) => p.mapBounds),
    take(1),
    rxMap((b) => [b.southwest, b.northeast]),
  )

  private makeFeatureCollection = (tasks: BoardTask[], color = 'gray') => {
    return {
      type: 'FeatureCollection',
      features: tasks.map((task, index) => {
        const coordinates: number[] = task.geometry?.coordinates ?? [0, 0]
        return {
          id: task.id,
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates,
          },
          properties: {
            id: task.id,
            task: this.makeTaskPopupModel(task, coordinates),
            color,
            text: index + 1,
          },
        }
      }) as Feature<Point, { [name: string]: unknown }>[],
    } as GeoJSON.FeatureCollection<GeoJSON.Geometry>
  }

  private makeTaskPopupModel = (task: BoardTask, coordinates: number[]) => {
    const { id, attachments, title, assignee, locationIdentifier, dueDate, asset, completor, dateCompleted } = task
    const subtitle = asset ? `${asset.layer?.title} ${asset.systemId}` : undefined
    const isPastDue = !dateCompleted && dueDate && moment(dueDate).isBefore(moment().startOf('d'))
    const isDueToday = !dateCompleted && dueDate && moment(dueDate).isSame(new Date(), 'day')
    const dueDateLabel = makeTaskDueDateLabel(task.dueDate)
    const dueDateUrgency = getTaskDueDateUrgency(task.dueDate, task.status ?? TaskStatus.Open)
    const imgUrl = attachments.length
      ? `https://api.ziptility.com/image-server/ziptility.assets/${attachments[0].id}/:/rs=w:500`
      : undefined
    return {
      id,
      task,
      imgUrl,
      title,
      subtitle,
      locationIdentifier,
      coordinates,
      assignee,
      dateCompleted,
      dueDate: dueDateLabel,
      dueDateUrgency,
      isPastDue,
      isDueToday,
      completor,
    }
  }

  async onMouseEnterLayer($event: MapMouseEvent) {
    const first = $event.features && $event.features.length && $event.features[0]
    if (!first || !first.properties || !first.properties?.id || !first.properties?.task) {
      return
    }
    const id = first.properties.id
    const task = this.taskBoardStore.allTasks().find((t) => t.id == id)
    if (!task) {
      return
    }
    this.taskPopup.set(task)
  }

  onMouseLeaveLayer() {
    this.taskPopup.set(undefined)
  }

  onClickLayer($event: MapMouseEvent) {
    const first = $event.features && $event.features.length && $event.features[0]
    if (!first) {
      this.taskPopup.set(undefined)
      return
    }

    if (first.properties?.id) {
      this.navToTask(first.properties.id)
    }
  }

  async _onMapLoad(map: Map) {
    this.map = map

    const preference = await firstValueFrom(this.preference$)
    if (!preference?.mapBounds) {
      this.initCenterFromMapCenter()
    }

    this.map.addControl(new ZipSelection())

    this.map.on('ziptility:selection', ({ bbox }) => {
      const layerIds = this.styleLayers.filter((l) => l.id === 'tasks').map((l) => l.id)
      const features = this.map.queryRenderedFeatures(bbox, { layers: layerIds })
      const ids = features.map((f) => f.id).filter((id) => !!id) as string[]

      this.selections.set(ids)

      ids.forEach((id) => {
        this.map.setFeatureState(
          {
            source: 'tasks',
            id,
          },
          { hover: true },
        )
      })

      this.onSelections.emit({ ids })
    })
  }

  initCenterFromMapCenter() {
    const selectedMap = this.selectedMap()
    if (!selectedMap || !selectedMap.mapCenter) {
      console.warn('missing user defined bounds or default map center')
      return
    }
    const { lng, lat, zoom } = selectedMap.mapCenter
    this.map.setZoom(zoom ?? 12)

    if (lng && lat) {
      this.map.setCenter([lng, lat])
    }
  }

  onMapClick(event: MapMouseEvent) {
    if (event.originalEvent.shiftKey) {
      return
    }
    const annotations = this.map.queryRenderedFeatures(event.point, {
      layers: this.styleLayers.map((l) => l.id),
    })
    if (!annotations.length) {
      this.selections().forEach((id) => {
        this.map.setFeatureState(
          {
            source: 'tasks',
            id,
          },
          { hover: false },
        )
      })

      this.selections.set([])
      this.onSelections.emit({ ids: [] })

      return
    }
  }

  onResetCenter() {
    const selectedMap = this.selectedMap()
    if (!selectedMap || !selectedMap.mapCenter) {
      return
    }
    const { lng, lat, zoom } = selectedMap.mapCenter
    this.map.setZoom(zoom ?? 12)
    this.map.setCenter([lng, lat])
  }

  navToTask(id: string) {
    this.router.navigate(['../', { outlets: { task: [id] } }], { relativeTo: this.route })
  }
}
